import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Newspaper, Plus, Edit2, Trash2, Copy, Search, Eye, EyeOff, Star, Calendar,
  User, TrendingUp, LayoutGrid, ArrowUpDown, CheckSquare, Square, ClipboardCheck,
  FileText, Filter, Clock, Zap
} from 'lucide-react';
import { JewelButton } from '../../components/ui/jewel-modal';
import { NewsComposer } from '../../components/content/NewsComposer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/uploads/')) return `${BACKEND_URL}${path}`;
  if (path.startsWith('/uploads/')) return `${BACKEND_URL}/api${path}`;
  return `${BACKEND_URL}${path}`;
};

const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest', fn: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0) },
  { id: 'oldest', label: 'Oldest', fn: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0) },
  { id: 'title',  label: 'A → Z',  fn: (a, b) => (a.title || '').localeCompare(b.title || '') },
];

const NewsManagementPage = ({ getAuthHeaders, currentUser }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | live | draft | featured
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortId, setSortId] = useState('newest');
  const [selected, setSelected] = useState(new Set());
  const [failedImages, setFailedImages] = useState({});

  const canManage = ['executive', 'superadmin'].includes(currentUser?.role);

  const fetchArticles = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/news`, { headers });
      setArticles(res.data);
    } catch { toast.error('Failed to load articles'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchArticles(); }, []);

  if (!canManage) {
    return <div className="flex items-center justify-center h-96"><p className="text-white/40">Access denied. Executives & Superadmins only.</p></div>;
  }

  const openNew = () => { setEditing(null); setComposerOpen(true); };
  const openEdit = (a) => { setEditing(a); setComposerOpen(true); };
  const duplicate = async (a) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/news/${a.id}/duplicate`, {}, { headers });
      toast.success('Duplicated as draft');
      fetchArticles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (id) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/admin/news/${id}`, { headers });
      toast.success('Deleted'); setDeleteConfirm(null); fetchArticles();
    } catch { toast.error('Failed to delete'); }
  };
  const togglePublished = async (a) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/admin/news/${a.id}`, { ...a, published: !a.published }, { headers });
      toast.success(a.published ? 'Unpublished' : 'Published');
      fetchArticles();
    } catch { toast.error('Failed'); }
  };
  const bulk = async (action) => {
    if (selected.size === 0) return;
    if (action === 'delete' && !window.confirm(`Delete ${selected.size} articles?`)) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/news/bulk-action`,
        { article_ids: Array.from(selected), action }, { headers });
      toast.success(`${res.data.affected} articles ${action.replace('_', ' ')}d`);
      setSelected(new Set());
      fetchArticles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed'); }
  };
  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const categories = useMemo(() => {
    const set = new Set(articles.map(a => a.category).filter(Boolean));
    return Array.from(set).sort();
  }, [articles]);

  const filtered = useMemo(() => {
    let out = articles;
    if (filter === 'live') out = out.filter(a => a.published);
    else if (filter === 'draft') out = out.filter(a => !a.published);
    else if (filter === 'featured') out = out.filter(a => a.is_featured);
    if (categoryFilter) out = out.filter(a => a.category === categoryFilter);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(a =>
        (a.title || '').toLowerCase().includes(q) ||
        (a.content || '').toLowerCase().includes(q) ||
        (a.tags || []).join(' ').includes(q)
      );
    }
    const s = SORT_OPTIONS.find(o => o.id === sortId) || SORT_OPTIONS[0];
    return [...out].sort(s.fn);
  }, [articles, filter, categoryFilter, search, sortId]);

  const stats = {
    total: articles.length,
    live: articles.filter(a => a.published).length,
    drafts: articles.filter(a => !a.published).length,
    featured: articles.filter(a => a.is_featured).length,
  };

  return (
    <div className="space-y-6 relative" data-testid="news-management-page">
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute -top-32 left-1/4 w-[500px] h-[300px] bg-cyan-500/[0.08] rounded-full blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[280px] bg-violet-500/[0.06] rounded-full blur-[120px]" />
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0a0a14] via-[#0d0b1f] to-[#0a0a14] p-6 md:p-7">
        <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] bg-cyan-500/[0.10] rounded-full blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-violet-500/[0.08] rounded-full blur-[100px]" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="relative w-14 h-14 flex-shrink-0">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-cyan-500/30" />
              <div className="absolute inset-0 rounded-2xl flex items-center justify-center">
                <Newspaper className="w-7 h-7 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 font-medium mb-1">Content · News</p>
              <h1 className="text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight">
                News <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">Studio</span>
              </h1>
              <p className="text-white/50 text-sm mt-1">Craft stories fast · AI-drafted content · live preview · bulk actions</p>
            </div>
          </div>
          <button onClick={openNew} data-testid="create-article-btn"
            className="group relative inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 text-white text-xs font-semibold shadow-[0_10px_30px_-10px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-all overflow-hidden">
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <Plus className="w-3.5 h-3.5" /> New article
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total',    value: stats.total,    hex: '#00E5FF', Icon: Newspaper },
          { label: 'Live',     value: stats.live,     hex: '#10B981', Icon: Eye },
          { label: 'Drafts',   value: stats.drafts,   hex: '#94A3B8', Icon: EyeOff },
          { label: 'Featured', value: stats.featured, hex: '#F59E0B', Icon: Star },
        ].map(s => (
          <div key={s.label} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 group hover:border-white/[0.15] transition-all">
            <div className="pointer-events-none absolute -top-8 -right-8 w-20 h-20 rounded-full blur-3xl opacity-30 group-hover:opacity-60 transition-opacity" style={{ background: s.hex }} />
            <div className="relative">
              <s.Icon className="w-4 h-4 mb-2" style={{ color: s.hex }} />
              <p className="text-2xl font-light text-white tabular-nums leading-none">{s.value}</p>
              <p className="text-[9px] tracking-[0.15em] uppercase text-white/40 mt-1.5">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.02] border border-white/[0.06]">
            {[
              { id: 'all',      label: 'All',      count: stats.total,    hex: '#00E5FF' },
              { id: 'live',     label: 'Live',     count: stats.live,     hex: '#10B981' },
              { id: 'draft',    label: 'Drafts',   count: stats.drafts,   hex: '#94A3B8' },
              { id: 'featured', label: 'Featured', count: stats.featured, hex: '#F59E0B' },
            ].map(t => {
              const a = filter === t.id;
              return (
                <button key={t.id} onClick={() => setFilter(t.id)} data-testid={`news-filter-${t.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all ${
                    a ? 'text-white border border-white/20' : 'text-white/50 hover:text-white border border-transparent'
                  }`}
                  style={a ? { background: `linear-gradient(135deg, ${t.hex}22, ${t.hex}0a)`, boxShadow: `0 0 14px -4px ${t.hex}` } : {}}>
                  <span>{t.label}</span>
                  <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold ${a ? 'bg-white/20 text-white' : 'bg-white/[0.04] text-white/50'}`}>{t.count}</span>
                </button>
              );
            })}
          </div>
          {categories.length > 0 && (
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="news-category-filter"
              className="px-3 py-1.5 rounded-full bg-white/[0.02] border border-white/[0.06] text-white text-[11px] focus:outline-none focus:border-cyan-400/40">
              <option value="">All categories</option>
              {categories.map(c => <option key={c}>{c}</option>)}
            </select>
          )}
          <div className="flex items-center gap-1 ml-auto">
            <ArrowUpDown className="w-3 h-3 text-white/40" />
            <select value={sortId} onChange={(e) => setSortId(e.target.value)}
              data-testid="news-sort"
              className="px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.06] text-white text-[11px] focus:outline-none focus:border-cyan-400/40">
              {SORT_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, content, tags…"
            data-testid="news-search"
            className="w-full pl-11 pr-4 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-full text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-400/40" />
        </div>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="sticky top-4 z-20 rounded-2xl border border-cyan-400/40 bg-gradient-to-r from-cyan-500/[0.10] to-violet-500/[0.10] backdrop-blur-md p-3 flex items-center gap-2 flex-wrap" data-testid="news-bulk-bar">
          <ClipboardCheck className="w-4 h-4 text-cyan-300" />
          <p className="text-white/90 text-sm font-medium">{selected.size} selected</p>
          <div className="flex items-center gap-1.5 ml-auto flex-wrap">
            <BulkBtn onClick={() => bulk('publish')} testid="bulk-publish" hex="#10B981" icon={Eye} label="Publish" />
            <BulkBtn onClick={() => bulk('unpublish')} testid="bulk-unpublish" hex="#94A3B8" icon={EyeOff} label="Unpublish" />
            <BulkBtn onClick={() => bulk('mark_featured')} testid="bulk-featured" hex="#F59E0B" icon={Star} label="Feature" />
            <BulkBtn onClick={() => bulk('delete')} testid="bulk-delete" hex="#EF4444" icon={Trash2} label="Delete" />
            <button onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/[0.06] text-[11px] font-medium transition-all">Clear</button>
          </div>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="text-center py-16 text-white/40 text-sm">Loading articles…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/[0.08] border border-cyan-500/20 mb-4">
            <Newspaper className="w-7 h-7 text-cyan-300" strokeWidth={1.5} />
          </div>
          <p className="text-white/70 tracking-wide">{search || filter !== 'all' ? 'No matches' : 'No articles yet'}</p>
          <p className="text-white/35 text-xs mt-1">
            {search || filter !== 'all' ? 'Adjust your filters or search.' : 'Tap "New article" to write your first story.'}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(a => (
            <ArticleCard key={a.id} a={a}
              isSelected={selected.has(a.id)}
              onToggleSelect={() => toggleSelect(a.id)}
              onEdit={() => openEdit(a)}
              onDelete={() => setDeleteConfirm(a)}
              onDuplicate={() => duplicate(a)}
              onTogglePublished={() => togglePublished(a)}
              failedImages={failedImages}
              setFailedImages={setFailedImages}
            />
          ))}
        </div>
      )}

      <NewsComposer
        open={composerOpen}
        onOpenChange={setComposerOpen}
        editing={editing}
        getAuthHeaders={getAuthHeaders}
        onSaved={() => { setComposerOpen(false); setEditing(null); fetchArticles(); }}
      />

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="max-w-sm w-full rounded-2xl border border-rose-500/30 bg-[#0a0a14] p-6" data-testid="news-delete-confirm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-rose-300" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Delete article?</h3>
                <p className="text-white/50 text-xs">"{deleteConfirm.title}"</p>
              </div>
            </div>
            <p className="text-white/60 text-sm mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <JewelButton variant="ghost" onClick={() => setDeleteConfirm(null)}>Cancel</JewelButton>
              <JewelButton accent="rose" onClick={() => remove(deleteConfirm.id)} icon={Trash2} data-testid="news-delete-confirm-btn">Delete</JewelButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BulkBtn = ({ onClick, testid, hex, icon: Icon, label }) => (
  <button onClick={onClick} data-testid={testid}
    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-all"
    style={{ background: `${hex}15`, borderColor: `${hex}40`, color: hex }}>
    <Icon className="w-3 h-3" /> {label}
  </button>
);

const ArticleCard = ({ a, isSelected, onToggleSelect, onEdit, onDelete, onDuplicate, onTogglePublished, failedImages, setFailedImages }) => (
  <div data-testid={`article-card-${a.id}`}
    className={`group relative overflow-hidden rounded-2xl border transition-all ${
      isSelected
        ? 'border-cyan-400/60 bg-gradient-to-br from-cyan-500/[0.10] via-white/[0.02] to-violet-500/[0.06] shadow-[0_0_25px_-8px_rgba(0,229,255,0.4)]'
        : a.published
          ? 'border-white/[0.08] bg-white/[0.02] hover:border-cyan-400/30 hover:shadow-[0_0_20px_-10px_rgba(0,229,255,0.35)]'
          : 'border-white/[0.05] bg-white/[0.01] opacity-70 hover:opacity-100'
    }`}>
    <button onClick={onToggleSelect} data-testid={`news-select-${a.id}`}
      className="absolute top-3 left-3 z-10 w-6 h-6 rounded-md border flex items-center justify-center transition-all"
      style={{
        background: isSelected ? '#00E5FF22' : 'rgba(255,255,255,0.02)',
        borderColor: isSelected ? '#00E5FF80' : 'rgba(255,255,255,0.15)',
      }}>
      {isSelected ? <CheckSquare className="w-3.5 h-3.5 text-cyan-300" /> : <Square className="w-3.5 h-3.5 text-white/30" />}
    </button>

    <div className="relative h-40 bg-slate-900 overflow-hidden">
      {a.image_url && !failedImages[a.id] ? (
        <img src={getImgUrl(a.image_url)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={() => setFailedImages(p => ({ ...p, [a.id]: true }))} />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
          <FileText className="w-10 h-10 text-white/15" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <div className="absolute top-3 right-3 flex gap-1.5">
        {a.is_featured && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-amber-500/25 border border-amber-400/50 text-amber-100 backdrop-blur-sm"><Star className="w-2.5 h-2.5" /> Featured</span>}
        {!a.published && <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-white/[0.10] border border-white/[0.20] text-white/80 backdrop-blur-sm">Draft</span>}
      </div>
      {a.category && (
        <div className="absolute bottom-3 left-3">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-black/60 border border-white/[0.15] text-white backdrop-blur-sm">{a.category}</span>
        </div>
      )}
    </div>

    <div className="p-4">
      <h3 className="text-white font-semibold text-[15px] leading-tight line-clamp-2 mb-1">{a.title}</h3>
      <p className="text-white/45 text-[12px] line-clamp-2 mb-3">{a.excerpt || (a.content || '').substring(0, 120)}</p>

      <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-[10px] text-white/40">
          <span className="flex items-center gap-1"><User className="w-2.5 h-2.5" />{a.author_name}</span>
          <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{a.created_at ? new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}</span>
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <IconBtn onClick={onTogglePublished} testid={`toggle-pub-${a.id}`} title={a.published ? 'Unpublish' : 'Publish'} hex={a.published ? '#10B981' : '#94A3B8'}>
            {a.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </IconBtn>
          <IconBtn onClick={onDuplicate} testid={`duplicate-${a.id}`} title="Duplicate" hex="#00E5FF"><Copy className="w-3.5 h-3.5" /></IconBtn>
          <IconBtn onClick={onEdit} testid={`edit-${a.id}`} title="Edit" hex="#8A2BE2"><Edit2 className="w-3.5 h-3.5" /></IconBtn>
          <IconBtn onClick={onDelete} testid={`news-delete-${a.id}`} title="Delete" hex="#F43F5E"><Trash2 className="w-3.5 h-3.5" /></IconBtn>
        </div>
      </div>
    </div>
  </div>
);

const IconBtn = ({ onClick, testid, title, hex, children }) => (
  <button onClick={onClick} data-testid={testid} title={title}
    className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
    style={{ color: hex, background: `${hex}0F` }}
    onMouseEnter={(e) => e.currentTarget.style.background = `${hex}25`}
    onMouseLeave={(e) => e.currentTarget.style.background = `${hex}0F`}>
    {children}
  </button>
);

export default NewsManagementPage;
